// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import { useEffect, useState } from 'react';
import { App as CapApp } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { IS_NATIVE } from './platform.js';

// DECISION (Android hardware/gesture back): wire @capacitor/app's backButton to
// the browser history stack that React Router already maintains. When the event
// says we can go back we pop history; at a navigation root we exit the app —
// the platform-expected behavior. iOS's edge-swipe-back is handled natively by
// the WebView's history, so this listener is Android-only in effect (the event
// never fires on iOS).
export function useNativeBackButton(): void {
  useEffect(() => {
    if (!IS_NATIVE) return;
    let handle: PluginListenerHandle | undefined;
    void CapApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        void CapApp.exitApp();
      }
    }).then((h) => {
      handle = h;
    });
    return () => {
      void handle?.remove();
    };
  }, []);
}

// Online/offline status for the mobile offline indicator (Phase 10). Uses the
// standard browser events, which fire inside the Capacitor WebView too, so no
// extra native plugin is required.
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);
  return online;
}
